import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

export const AdverseEventTypeSchema     = z.enum(['SAR', 'SAE']);
export const AdverseEventSeveritySchema = z.enum(['minor', 'moderate', 'serious', 'critical']);
export const AdverseEventStatusSchema   = z.enum(['open', 'investigating', 'closed']);

export type AdverseEventDates = Partial<Record<
  'occurred_at' | 'detected_at' | 'investigation_started_at' | 'investigation_closed_at' | 'corrective_action_at' | 'resolved_at',
  Date | null
>>;

// Investigation and resolution dates cannot fall before the occurred or detected date.
export function hasFollowUpDateBeforeEvent(d: AdverseEventDates): boolean {
  const eventDates = [d.occurred_at, d.detected_at].filter((date): date is Date => !!date);
  return [d.investigation_started_at, d.investigation_closed_at, d.corrective_action_at, d.resolved_at]
    .some((date) => !!date && eventDates.some((eventDate) => date < eventDate));
}

// Investigation and resolution dates cannot be in the future.
export function hasFollowUpDateInFuture(d: AdverseEventDates): boolean {
  const now = new Date();
  return [d.investigation_started_at, d.investigation_closed_at, d.corrective_action_at, d.resolved_at]
    .some((date) => !!date && date > now);
}

// On create, null means "not set". Without this, z.coerce.date() turns null into 1970-01-01.
const optionalCreateDate = z.preprocess((v) => (v === null ? undefined : v), z.coerce.date().optional());

// hospital_id is resolved by the controller from the caller's decrypted token
// (getScopeId), not a client-supplied field.
export const CreateAdverseEventSchema = z.object({
  type:                AdverseEventTypeSchema,
  category:            z.string().min(1).max(100),
  description:         z.string().optional(),
  occurred_at:         z.coerce.date(),
  detected_at:         optionalCreateDate,
  affected_batch_id:   z.string().max(50).optional(),
  affected_patient_id: uuidSchema.optional(),
  affected_donor_id:   uuidSchema.optional(),
  status:                       AdverseEventStatusSchema.optional(),
  investigation_started_at:     optionalCreateDate,
  investigation_closed_at:      optionalCreateDate,
  corrective_action_at:         optionalCreateDate,
  resolved_at:                  optionalCreateDate,
  notified_authority:           z.boolean().optional(),
  notified_patient:             z.boolean().optional(),
}).refine(
  (d) => d.occurred_at <= new Date(),
  { message: 'reports.incident.errorOccurredAtFuture', path: ['occurred_at'] },
).refine(
  (d) => !d.detected_at || d.detected_at >= d.occurred_at,
  { message: 'reports.incident.errorDetectedBeforeOccurred', path: ['detected_at'] },
).refine(
  (d) => !hasFollowUpDateBeforeEvent(d),
  { message: 'reports.incident.errorFollowUpBeforeEvent', path: ['investigation_started_at'] },
).refine(
  (d) => !hasFollowUpDateInFuture(d),
  { message: 'reports.incident.errorFollowUpAtFuture', path: ['investigation_started_at'] },
);
export type CreateAdverseEventRequest = z.infer<typeof CreateAdverseEventSchema>;

// All fields optional. Backend only clears rank-specific fields when rolling back to a lower status;
// it never auto-sets them — the frontend pre-fills defaults before submitting.
export const UpdateAdverseEventSchema = z.object({
  type:                 AdverseEventTypeSchema.optional(),
  category:             z.string().min(1).max(100).optional(),
  status:               AdverseEventStatusSchema.optional(),
  occurred_at:          z.coerce.date().optional(),
  detected_at:          z.coerce.date().nullable().optional(),
  investigation_started_at: z.coerce.date().nullable().optional(),
  investigation_closed_at:  z.coerce.date().nullable().optional(),
  corrective_action_at:     z.coerce.date().nullable().optional(),
  resolved_at:              z.coerce.date().nullable().optional(),
  affected_batch_id:        z.string().max(50).nullable().optional(),
  affected_patient_id:      uuidSchema.nullable().optional(),
  affected_donor_id:        uuidSchema.nullable().optional(),
  description:              z.string().nullable().optional(),
  notified_authority:       z.boolean().optional(),
  notified_patient:         z.boolean().optional(),
}).refine(
  (d) => !d.occurred_at || d.occurred_at <= new Date(),
  { message: 'reports.incident.errorOccurredAtFuture', path: ['occurred_at'] },
).refine(
  (d) => !d.occurred_at || !d.detected_at || d.detected_at >= d.occurred_at,
  { message: 'reports.incident.errorDetectedBeforeOccurred', path: ['detected_at'] },
).refine(
  (d) => !hasFollowUpDateBeforeEvent(d),
  { message: 'reports.incident.errorFollowUpBeforeEvent', path: ['investigation_started_at'] },
).refine(
  (d) => !hasFollowUpDateInFuture(d),
  { message: 'reports.incident.errorFollowUpAtFuture', path: ['investigation_started_at'] },
);
export type UpdateAdverseEventRequest = z.infer<typeof UpdateAdverseEventSchema>;

export const AdverseEventResponseSchema = z.object({
  id:                       z.number(),
  display_id:               z.string().nullable(),
  hospital_id:              z.string(),
  type:                     AdverseEventTypeSchema,
  category:                 z.string(),
  severity:                 AdverseEventSeveritySchema,
  description:              z.string().nullable(),
  status:                   AdverseEventStatusSchema,
  affected_batch_id:        z.string().nullable(),
  affected_patient_id:      z.string().nullable(),
  affected_donor_id:        z.string().nullable(),
  reported_by:              z.string().nullable(),
  occurred_at:              z.string(),
  detected_at:              z.string().nullable(),
  investigation_started_at: z.string().nullable(),
  investigation_closed_at:  z.string().nullable(),
  corrective_action_at:     z.string().nullable(),
  resolved_at:              z.string().nullable(),
  notified_authority:       z.boolean(),
  notified_patient:         z.boolean(),
  created_at:               z.string(),
  updated_at:               z.string(),
  deleted_at:               z.string().nullable(),
});
export type AdverseEventResponse = z.infer<typeof AdverseEventResponseSchema>;

export const AdverseEventSortBySchema = z.enum([
  'display_id', 'type', 'category', 'severity', 'status', 'occurred_at', 'detected_at',
]);

export const AdverseEventListQuerySchema = z.object({
  type:        AdverseEventTypeSchema.optional(),
  status:      AdverseEventStatusSchema.optional(),
  severity:    AdverseEventSeveritySchema.optional(),
  category:    z.string().optional(),
  display_id:  z.string().optional(),
  from:          z.coerce.date().optional(),
  to:            z.coerce.date().optional(),
  detected_from: z.coerce.date().optional(),
  detected_to:   z.coerce.date().optional(),
  sort_by:     AdverseEventSortBySchema.optional().default('occurred_at'),
  sort_order:  z.enum(['asc', 'desc']).optional().default('desc'),
  page:        z.coerce.number().int().min(1).default(1),
  limit:       z.coerce.number().int().min(1).max(100).default(20),
});
export type AdverseEventListQuery = z.infer<typeof AdverseEventListQuerySchema>;

export const AdverseEventListResponseSchema = z.object({
  data:  z.array(AdverseEventResponseSchema),
  total: z.number(),
  page:  z.number(),
  limit: z.number(),
});

export const AdverseEventCategorySchema = z.object({
  id:       z.number(),
  name:     z.string(),
  severity: z.string(),
});
export type AdverseEventCategory = z.infer<typeof AdverseEventCategorySchema>;
